'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import { connect } from 'react-redux';

import imge from '@publicImages/PageEnd-576@1x.png';

import SailorAnimation from '@/components/Animations/SailorAnimation';
import Button from '@/components/elements/Button';
import IconButton from '@/components/elements/IconButton';
import { ThreeDotsLoaderContent } from '@/components/elements/loaders/ThreeDotsContent';
import Minus from '@/components/Icon/Minus';
import Plus from '@/components/Icon/Plus';
import Sailors from '@/components/Icon/Sailors';
import PromoBannerPlace from '@/components/PromoBanner/Place';
import useCustomArrowKeys from '@/ducks/a11y/hooks/useCustomArrowKeys';
import { useUIResourcePool } from '@/ducks/common/resources/index';
import { selectAccessible, selectCabins, selectSailors } from '@/ducks/filters/selectors';
import { setFilter } from '@/ducks/filters/setters';
import { getDefaultFilters, getMaxSailors } from '@/ducks/filtersOptions';
import { toCategoryPage } from '@/ducks/voyageInfo/category/navigation';
import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import useFlowType from '@/features/flowType/useFlowType';
import { PromoBannerPlaceId } from '@/features/promoBanners/types';
import useKeyboardFocus from '@/hooks/useKeyboardFocus';
import useOnKeyDown from '@/hooks/useOnKeyDown';
import useSafariNavbarOverlapToggle from '@/hooks/useSafariNavbarOverlapToggle';
import ChatbotPositioningElement from '@/layouts/Wrappers/Chatbot/ChatbotPositioningElement';
import {
  selectIsCabinCategoriesError,
  selectIsCabinCategoriesIsLoading,
  selectIsCabinCategoriesLoaded,
} from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/selectors';
import { useAppDispatch, useAppSelector } from '@/store';
import tagmanager from '@/tagmanager';

import NewVoyageAccessibility from '../NewVoyageAccessibility';
import TravelPartyCabinDescription from '../TravelPartyMultiCabinDes';

import './NewVoyageTravelParty.scss';

const NEW_VOYAGE_TRAVEL_PARTY_TEXT = {
  cabinFeature: 'Precheckout.sailorsModule.cabinFeature',
  sailorsCountTitle: 'Precheckout.sailorsModule.heading',
  sailorsText: 'Precheckout.sailorsModule.sailors',
  viewCabinsBtn: 'Precheckout.sailorsModule.cabinsCTA',
};

const NewVoyageTravelParty = ({
  defaultSailors,
  isAccessible,
  maxSailors,
  onDisabledSailorModalClick,
  sailingData,
  sailors,
  setSailors,
}) => {
  useSafariNavbarOverlapToggle();
  const dispatch = useAppDispatch();

  const isCabinCategoriesIsLoading = useAppSelector(selectIsCabinCategoriesIsLoading);
  const isCabinCategoriesIsLoaded = useAppSelector(selectIsCabinCategoriesLoaded);
  const haveCabinCategoriesError = useAppSelector((state) => !!selectIsCabinCategoriesError(state)?.type);

  const { cabinFeature, sailorsCountTitle, sailorsText, viewCabinsBtn } =
    useUIResourcePool(NEW_VOYAGE_TRAVEL_PARTY_TEXT);
  const isByKeyboard = useKeyboardFocus();
  const [isSailorCountReady, setIsSailorCountReady] = useState(false);

  const { isMNVV } = useFlowType();
  const buttonContainerRef = useRef(null);
  const [activeBtnIndex, setActiveBtnIndex] = useState(null);

  const onSailorSelectionMobile = useCallback(
    (value) => {
      let updateValue = sailors;
      if (sailors !== 0 && sailors < maxSailors && value === 'increase') {
        updateValue = sailors + 1;

        setSailors(updateValue);
      } else if (isMNVV && sailors === 2 && sailors <= maxSailors && value === 'decrease') {
        onDisabledSailorModalClick();
        return;
      } else if (sailors !== 1 && sailors <= maxSailors && value === 'decrease') {
        updateValue = sailors - 1;

        setSailors(updateValue);
      }

      setSailors(updateValue);
    },
    [sailors, maxSailors, isMNVV, onDisabledSailorModalClick, setSailors],
  );

  const onSailorSelectionDesk = useCallback(
    (event) => {
      const newSailors = Number(event.currentTarget.textContent);

      if (isMNVV && newSailors === 1) {
        onDisabledSailorModalClick();
        return;
      }

      setSailors(newSailors);
    },
    [isMNVV, onDisabledSailorModalClick, setSailors],
  );

  const handleKeyDown = useOnKeyDown(
    (event) => {
      onSailorSelectionDesk(event);
      setActiveBtnIndex(null);
    },
    { isDefaultPrevented: true },
  );

  useCustomArrowKeys({
    element: isSailorCountReady ? buttonContainerRef.current : null,
    elementsQuery: '.sailorBtn',
    focusableElementsQuery: '.sailorBtn:not(.active)',
    isHorizontalArrowsIncluded: true,
  });

  const renderSailorButtons = useCallback(() => {
    return (
      <div className="sailorBtnCentred" ref={buttonContainerRef}>
        {Array.from({ length: maxSailors }).map((_, index) => {
          const sailorNumber = index + 1;
          const isActiveBtn = sailorNumber === sailors;

          if (activeBtnIndex === null && isActiveBtn) {
            setActiveBtnIndex(index);
          }

          const sailorClassNames = classNames('sailorBtn', {
            active: isActiveBtn,
            keyboardFocus: isByKeyboard,
            mnvvSailorBtnDisabled: isMNVV && sailorNumber === 1,
          });

          return (
            <div
              aria-checked={isActiveBtn}
              className={sailorClassNames}
              key={sailorNumber}
              onClick={onSailorSelectionDesk}
              onKeyDown={handleKeyDown}
              role="radio"
              tabIndex={0}
            >
              <span>{sailorNumber}</span>
            </div>
          );
        })}
      </div>
    );
  }, [activeBtnIndex, maxSailors, sailors, isMNVV, onSailorSelectionDesk, handleKeyDown, isByKeyboard]);

  const onSailorSelectionDec = useCallback(() => {
    onSailorSelectionMobile('decrease');
  }, [onSailorSelectionMobile]);

  const onSailorSelectionInc = useCallback(() => {
    onSailorSelectionMobile('increase');
  }, [onSailorSelectionMobile]);

  const onViewCabin = useCallback(() => {
    const values = [`${sailors}_accessible`, isAccessible];
    tagmanager.tracker.voyagesFilter.trackApplyFilter({
      name: tagmanager.trackerConstants.EVENT_LABELS.VOYAGE_FILTERS.FILTER_NAMES.TRAVEL_PARTY,
      values,
    });

    const buttonText = `cta-${kebabCase(viewCabinsBtn?.[0]?.trim())}`;
    tagmanager.tracker.common.trackButtonClick({
      label: buttonText,
    });

    dispatch(
      tagmanager.tracker.ECommercePromotions.measuringProductClicks({
        labels: [tagmanager.trackerConstants.LISTS.YOUR_VOYAGE],
        list: tagmanager.trackerConstants.LISTS.YOUR_VOYAGE,
        sailing: sailingData,
      }),
    );
    toCategoryPage();
  }, [sailors, isAccessible, dispatch, sailingData]);

  const isViewCabinsButtonLoading = isCabinCategoriesIsLoading || !isCabinCategoriesIsLoaded;
  const isViewCabinsButtonDisabled = isViewCabinsButtonLoading || haveCabinCategoriesError;
  const ToChooseVoyagePageBtn = (
    <div className="NewTravelParty__viewCabin">
      <div className={classNames({ viewBtnLink: isViewCabinsButtonDisabled })} tabIndex={-1}>
        <Button className="viewCabin" disabled={isViewCabinsButtonDisabled} onClick={onViewCabin} variant="primary">
          {isViewCabinsButtonLoading ? <ThreeDotsLoaderContent /> : viewCabinsBtn}
        </Button>
      </div>
    </div>
  );

  const IconClassNames = classNames('sailorBtn', {
    keyboardFocus: isByKeyboard,
  });

  useEffect(() => {
    if (buttonContainerRef.current) {
      setIsSailorCountReady(true);
    }
  }, [buttonContainerRef]);

  return (
    <div className="NewTravelParty">
      {/*card view only for desktop*/}
      <div className="NewTravelParty__card">
        <fieldset>
          <legend>
            <div className="NewTravelParty__heading">
              <h2 className="NewTravelParty__title">{sailorsCountTitle}</h2>
            </div>
          </legend>
          <div className="NewTravelParty__selectionModule">
            <div className="sailorAnimations">
              <SailorAnimation defaultSailor={defaultSailors} selectedSailor={sailors} />
            </div>
            <div className="renderSailorButtons">{renderSailorButtons()}</div>
          </div>
          <NewVoyageAccessibility />
        </fieldset>
        {ToChooseVoyagePageBtn}
        <PromoBannerPlace placeId={PromoBannerPlaceId.preCheckoutD} />
        <TravelPartyCabinDescription />
      </div>

      <div className="NewTravelParty__bottomBar">
        <div className="NewTravelParty__title">
          <h2>{sailorsCountTitle}</h2>
          <div className="verticalLine" />
          <div className="gradientImage">
            <img alt="Gradient" src={imge} />
          </div>
        </div>

        <ChatbotPositioningElement className="NewTravelParty__main">
          <div className="NewTravelParty__selection">
            <div className="sailorSelection">
              <span className="sailorIcon">
                <Sailors />
              </span>

              <span className="text">{sailorsText}</span>
              <section className="icon">
                <IconButton
                  aria-label="decrease sailor count"
                  border
                  className={IconClassNames}
                  onClick={onSailorSelectionDec}
                >
                  <Minus />
                </IconButton>
                {sailors}
                <IconButton
                  aria-label="increase Sailor count"
                  border
                  className={IconClassNames}
                  onClick={onSailorSelectionInc}
                >
                  <Plus />
                </IconButton>
              </section>
            </div>
            <div className="break" />
            <div className="AccesscabinSelection">
              <span className="text"> {cabinFeature}</span>
              <NewVoyageAccessibility />
            </div>
          </div>
          {ToChooseVoyagePageBtn}
        </ChatbotPositioningElement>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    cabins: selectCabins(state),
    defaultSailors: getDefaultFilters(state).sailors,
    isAccessible: selectAccessible(state),
    maxSailors: getMaxSailors(state),
    sailingData: selectSailingData(state),
    sailors: selectSailors(state),
    setSailors: setFilter.sailors,
  };
}

export default connect(mapStateToProps)(NewVoyageTravelParty);
